// ============================================================
//  src/pages/auth/LoginPage.js
// ============================================================
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getError } from '../../utils/helpers';
import './auth.css';

export default function LoginPage() {
  const { login }     = useAuth();
  const nav           = useNavigate();
  const loc           = useLocation();
  const [form, setForm]       = useState({ identifier: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await login(form.identifier.trim(), form.password);
      toast.success('Welcome back.');
      const next = loc.state?.from || '/dashboard';
      nav(next, { replace: true });
    } catch (err) {
      // Unverified email → redirect to verify page with the email prefilled
      const status = err?.response?.status;
      const extra  = err?.response?.data || {};
      if (status === 403 && extra?.email && err?.response?.data?.code !== 'banned') {
        nav('/verify-email', { state: { email: extra.email } });
        return;
      }
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* ── Brand panel ── */}
      <div className="auth-left">
        <div className="auth-left-main">
          <div className="auth-brand">
            <span className="auth-brand-dot">C</span>
            <span>CommandKit</span>
          </div>
          <div>
            <div className="auth-headline">Build advanced<br /><em>bots without limits.</em></div>
            <p className="auth-sub">
              Visual builder for beginners. JavaScript and Python for pros.
              Ship Telegram and WhatsApp bots that scale.
            </p>
            <ul className="auth-features">
              <li><i className="fas fa-check" /> Unlimited bots on Pro</li>
              <li><i className="fas fa-check" /> One-click GitHub sync</li>
              <li><i className="fas fa-check" /> Sell on the CommandKit Store</li>
              <li><i className="fas fa-check" /> Real-time logs & analytics</li>
            </ul>
          </div>
          <div className="auth-footer-badge">
            <i className="fas fa-lock" /> 256-bit SSL · GDPR ready
          </div>
        </div>
      </div>

      {/* ── Form ── */}
      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-mobile-brand">
            <span className="auth-brand-dot">C</span> CommandKit
          </div>

          <div className="auth-hdr">
            <h2>Welcome back</h2>
            <p>Sign in to manage your bots.</p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <div className="auth-field">
              <label>Email or phone</label>
              <div className="auth-field-wrap">
                <i className="fas fa-user fi" />
                <input
                  type="text"
                  value={form.identifier}
                  onChange={set('identifier')}
                  placeholder="you@example.com"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <label>Password</label>
              <div className="auth-field-wrap">
                <i className="fas fa-lock fi" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button type="button" className="pw-toggle" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>
                  <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </div>

            <div className="auth-row">
              <span />
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <button type="submit" className="btn-auth" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Signing in…</>
                : <><i className="fas fa-arrow-right-to-bracket" /> Sign in</>
              }
            </button>
          </form>

          <div className="auth-bottom">
            New to CommandKit? <Link to="/register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}